import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSEO } from "../hooks/useSEO";

function formatCount(n) {
  if (!n) return null;
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(".0", "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(".0", "") + "K";
  return String(n);
}

function SocialLink({ href, label }) {
  if (!href) return null;
  const url = href.startsWith("http") ? href : `https://${href.replace(/^@/, "")}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="label"
      style={{ color: "#A1A1AA", borderBottom: "1px solid #3a3a3a", paddingBottom: 2, transition: "color 0.3s, border-color 0.3s" }}
      onMouseEnter={(e) => { e.currentTarget.style.color = "#C9A961"; e.currentTarget.style.borderBottomColor = "#C9A961"; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = "#A1A1AA"; e.currentTarget.style.borderBottomColor = "#3a3a3a"; }}
    >
      {label}
    </a>
  );
}

function ArtistCard({ artist }) {
  const [hovered, setHovered] = useState(false);

  const stats = [
    artist.instagram_followers && { label: "IG", value: formatCount(artist.instagram_followers) },
    artist.tiktok_followers && { label: "TK", value: formatCount(artist.tiktok_followers) },
    artist.youtube_subscribers && { label: "YT", value: formatCount(artist.youtube_subscribers) },
  ].filter(Boolean);

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ transition: "opacity 0.4s" }}
    >
      {/* Portrait */}
      <div
        className="overflow-hidden"
        style={{ aspectRatio: "3 / 4", background: "#111", position: "relative" }}
      >
        {artist.portrait ? (
          <img
            src={artist.portrait}
            alt={artist.name}
            className="w-full h-full object-cover"
            loading="lazy"
            style={{
              filter: hovered ? "grayscale(0%)" : "grayscale(100%)",
              transform: hovered ? "scale(1.03)" : "scale(1)",
              transition: "filter 700ms ease, transform 900ms ease",
            }}
          />
        ) : (
          <svg width="100%" height="100%" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <rect width="300" height="400" fill="#0d0d0d" />
            <rect x="0" y="0" width="300" height="2" fill="#C9A961" opacity="0.2" />
            <rect x="0" y="398" width="300" height="2" fill="#C9A961" opacity="0.2" />
            <text x="150" y="200" textAnchor="middle" dominantBaseline="middle"
              fontFamily="Cormorant Garamond, Georgia, serif" fontSize="44" fontStyle="italic"
              fill="#C9A961" opacity="0.6">{artist.name}</text>
          </svg>
        )}
        {artist.portrait_credit && (
          <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "5px 10px", background: "rgba(0,0,0,0.52)", fontFamily: "Inter, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.38)", display: "block" }}>
            © {artist.portrait_credit}
          </span>
        )}
      </div>

      {/* Infos */}
      <div className="mt-5">
        <h3
          className="font-serif"
          style={{ fontSize: "clamp(20px, 2vw, 26px)", lineHeight: 1.1, color: hovered ? "#C9A961" : "#F8F6F0", transition: "color 0.4s" }}
        >
          {artist.name}
        </h3>

        {/* Tags */}
        {artist.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {artist.tags.map((t) => (
              <span
                key={t}
                className="label"
                style={{ fontSize: 9, letterSpacing: "0.16em", color: "#C9A961", border: "1px solid #2a2a2a", padding: "3px 8px" }}
              >
                {t.toUpperCase()}
              </span>
            ))}
          </div>
        )}

        {/* Bio */}
        {artist.bio && (
          <p className="mt-4" style={{ fontSize: 14, color: "rgba(248,246,240,0.6)", lineHeight: 1.65, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {artist.bio}
          </p>
        )}

        {/* Stats réseaux */}
        {stats.length > 0 && (
          <div className="flex gap-6 mt-4">
            {stats.map(({ label, value }) => (
              <div key={label}>
                <p className="label" style={{ fontSize: 9, color: "#3a3a3a" }}>{label}</p>
                <p className="label" style={{ fontSize: 13, color: "#A1A1AA" }}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Liens sociaux */}
        <div className="flex flex-wrap gap-5 mt-5">
          {artist.instagram && <SocialLink href={`https://instagram.com/${artist.instagram.replace(/^@/, "")}`} label="Instagram" />}
          {artist.tiktok && <SocialLink href={`https://tiktok.com/@${artist.tiktok.replace(/^@/, "")}`} label="TikTok" />}
          {artist.youtube && <SocialLink href={`https://youtube.com/@${artist.youtube}`} label="YouTube" />}
        </div>
      </div>
    </article>
  );
}

export default function Bibliotheque() {
  useSEO("Bibliothèque", "Tous les artistes de l'humour francophone suivis par Le Stand d'Or — biographies, réseaux, chiffres.");

  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/artistes-biblio");
        setArtists(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Tous les tags disponibles
  const allTags = [...new Set(artists.flatMap((a) => a.tags || []))].sort();

  const filtered = artists.filter((a) => {
    const q = search.toLowerCase();
    const matchSearch = !q || a.name?.toLowerCase().includes(q) || a.bio?.toLowerCase().includes(q) || a.tags?.some((t) => t.toLowerCase().includes(q));
    const matchTag = !activeTag || a.tags?.includes(activeTag);
    return matchSearch && matchTag;
  });

  return (
    <div className="pt-[120px] pb-40">
      {/* Hero */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-12 pb-20" style={{ borderBottom: "1px solid #1c1c1c" }}>
        <p className="label mb-6" style={{ color: "#C9A961" }}>Le Stand d'Or — Édition 2026</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <h1
              className="font-serif"
              style={{ fontSize: "clamp(64px, 10vw, 160px)", lineHeight: 0.9, letterSpacing: "-0.03em", color: "#F8F6F0" }}
            >
              Biblio&shy;thèque
            </h1>
          </div>
          <div className="md:col-span-5">
            <p style={{ fontSize: "clamp(16px, 1.4vw, 20px)", color: "rgba(248,246,240,0.65)", lineHeight: 1.7 }}>
              L'ensemble des artistes de l'humour francophone suivis et analysés par notre comité de sélection. Biographies, présence digitale, œuvres.
            </p>
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-12 py-12 flex flex-wrap gap-6 items-center" style={{ borderBottom: "1px solid #1c1c1c" }}>
        {/* Recherche */}
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 400 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-editorial"
            placeholder="Rechercher un artiste…"
            style={{ paddingRight: 36 }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", fontSize: 16 }}
            >×</button>
          )}
        </div>

        {/* Tags */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setActiveTag("")}
              className="label"
              style={{ background: "none", border: `1px solid ${!activeTag ? "#C9A961" : "#2a2a2a"}`, color: !activeTag ? "#C9A961" : "#A1A1AA", padding: "5px 14px", cursor: "pointer", transition: "all 0.2s", fontSize: 10, letterSpacing: "0.15em" }}
            >
              TOUS
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(activeTag === t ? "" : t)}
                className="label"
                style={{ background: "none", border: `1px solid ${activeTag === t ? "#C9A961" : "#2a2a2a"}`, color: activeTag === t ? "#C9A961" : "#A1A1AA", padding: "5px 14px", cursor: "pointer", transition: "all 0.2s", fontSize: 10, letterSpacing: "0.15em" }}
              >
                {t.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        <p className="label ml-auto" style={{ color: "#3a3a3a" }}>
          {filtered.length} artiste{filtered.length !== 1 ? "s" : ""}
        </p>
      </section>

      {/* Grille */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-12 pt-16">
        {loading && (
          <p className="label" style={{ color: "#A1A1AA" }}>Chargement…</p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="label" style={{ color: "#A1A1AA" }}>Aucun artiste trouvé{search ? ` pour « ${search} »` : ""}.</p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-16">
          {filtered.map((a) => (
            <ArtistCard key={a.id} artist={a} />
          ))}
        </div>
      </section>
    </div>
  );
}
